import React from 'react';
import { Icon } from '@iconify/react';

interface ChannelDef {
  icon: string;
  label: string;
}

const PERSON_CHANNELS: Record<string, ChannelDef> = {
  email: { icon: 'solar:letter-linear', label: 'Email' },
  phone: { icon: 'solar:phone-linear', label: 'Phone' },
  mobile: { icon: 'solar:smartphone-linear', label: 'Mobile' },
  leadLinkedin: { icon: 'mdi:linkedin', label: 'LinkedIn' },
  leadWhatsapp: { icon: 'mdi:whatsapp', label: 'WhatsApp' },
  leadLine: { icon: 'simple-icons:line', label: 'LINE' },
  leadInstagram: { icon: 'mdi:instagram', label: 'Instagram' },
  leadMessenger: { icon: 'mdi:messenger', label: 'Messenger' },
  leadTwitter: { icon: 'mdi:twitter', label: 'Twitter/X' },
  leadFacebook: { icon: 'mdi:facebook', label: 'Facebook' },
  leadTiktok: { icon: 'ic:baseline-tiktok', label: 'TikTok' },
  leadYoutube: { icon: 'mdi:youtube', label: 'YouTube' },
  leadDiscord: { icon: 'ic:baseline-discord', label: 'Discord' },
  leadWechat: { icon: 'ic:baseline-wechat', label: 'WeChat' },
  leadGooglePlaces: { icon: 'mdi:google', label: 'Google Places' },
  leadTripadvisor: { icon: 'simple-icons:tripadvisor', label: 'TripAdvisor' },
};

const COMPANY_CHANNELS: Record<string, ChannelDef> = {
  companyEmail: { icon: 'solar:letter-linear', label: 'Company Email' },
  companyPhone: { icon: 'solar:phone-linear', label: 'Company Phone' },
  companyWebsite: { icon: 'solar:global-linear', label: 'Website' },
  companyLinkedin: { icon: 'mdi:linkedin', label: 'Company LinkedIn' },
  companyWhatsapp: { icon: 'mdi:whatsapp', label: 'Company WhatsApp' },
  companyLine: { icon: 'simple-icons:line', label: 'Company LINE' },
  companyInstagram: { icon: 'mdi:instagram', label: 'Company Instagram' },
  companyMessenger: { icon: 'mdi:messenger', label: 'Company Messenger' },
  companyTwitter: { icon: 'mdi:twitter', label: 'Company Twitter/X' },
  companyFacebook: { icon: 'mdi:facebook', label: 'Company Facebook' },
  companyTiktok: { icon: 'ic:baseline-tiktok', label: 'Company TikTok' },
  companyYoutube: { icon: 'mdi:youtube', label: 'Company YouTube' },
  companyDiscord: { icon: 'ic:baseline-discord', label: 'Company Discord' },
  companyWechat: { icon: 'ic:baseline-wechat', label: 'Company WeChat' },
  companyGooglePlaces: { icon: 'mdi:google', label: 'Company Google Places' },
  companyTripadvisor: { icon: 'simple-icons:tripadvisor', label: 'Company TripAdvisor' },
};

// Ordered priority for display (person channels first, then company)
const DISPLAY_ORDER = [
  ...Object.keys(PERSON_CHANNELS),
  ...Object.keys(COMPANY_CHANNELS),
];

interface ChannelIconsProps {
  lead: Record<string, unknown>;
  maxIcons?: number;
  size?: 'xs' | 'sm' | 'md';
  className?: string;
  /** Email verification data — when present, the email icon is colored by
   *  quality and shows the full verification card on hover. */
  emailProviderData?: unknown;
}

const sizeMap = {
  xs: { icon: 'h-3 w-3', wrapper: 'h-5 w-5', gap: 'gap-0.5' },
  sm: { icon: 'h-3.5 w-3.5', wrapper: 'h-6 w-6', gap: 'gap-1' },
  md: { icon: 'h-4 w-4', wrapper: 'h-7 w-7', gap: 'gap-1.5' },
};

export const ChannelIcons: React.FC<ChannelIconsProps> = ({
  lead,
  maxIcons = 6,
  size = 'sm',
  className = '',
  emailProviderData,
}) => {
  const s = sizeMap[size];
  const available: { key: string; def: ChannelDef; isPerson: boolean }[] = [];

  for (const key of DISPLAY_ORDER) {
    const personDef = PERSON_CHANNELS[key];
    const companyDef = COMPANY_CHANNELS[key];
    const def = personDef || companyDef;
    if (!def) continue;
    const value = lead[key];
    if (value && typeof value === 'string' && value.trim() && !['N/A', '#', 'Not available', 'Unknown'].includes(value.trim())) {
      available.push({ key, def, isPerson: !!personDef });
    }
  }

  if (available.length === 0) return null;

  const visible = available.slice(0, maxIcons);
  const remaining = available.length - maxIcons;

  return (
    <div className={`flex items-center ${s.gap} ${className}`} role="list" aria-label="Available channels">
      {visible.map(({ key, def }) => (
        <span
          key={key}
          role="listitem"
          title={def.label}
          className={`inline-flex ${s.wrapper} items-center justify-center rounded-md text-gray-500 hover:text-gray-300 transition-colors`}
        >
          <Icon icon={def.icon} className={s.icon} aria-hidden="true" />
        </span>
      ))}
      {remaining > 0 && (
        <span className={`inline-flex ${s.wrapper} items-center justify-center rounded-md text-[9px] font-medium text-gray-600`}>
          +{remaining}
        </span>
      )}
    </div>
  );
};
